import { Button } from "../components/Button";
import { Icon } from "../components/Icon";
import { Spinner } from "../components/Spinner";
import { TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger, } from "../components/Tooltip";
import { ChevronDown, ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import React, { useCallback, useEffect, useState, } from "react";
import { Checkbox } from "./Checkbox";
export function Tree({ children, isLoading, isBoxed = false, overflowVisible = false, tailwindIconTextColor, variant = "finder", className, }) {
    const modifiedChildren = React.Children.map(children, (child) => {
        // /!\ Limitation: This stops on the first invalid element.
        // Meaning that if Tree.Item is not the first child, it will not work.
        if (React.isValidElement(child)) {
            // Clone the child element and pass the necessary props
            const childProps = {};
            if (variant === "navigator") {
                childProps.isNavigatable = true;
            }
            if (tailwindIconTextColor) {
                childProps.tailwindIconTextColor = tailwindIconTextColor;
            }
            return React.cloneElement(child, childProps);
        }
        return child;
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: cn("s-flex s-flex-col s-gap-0.5", overflowVisible ? "s-overflow-visible" : "s-overflow-hidden", isBoxed &&
                "s-rounded-xl s-border s-border-border s-bg-muted-background s-px-3 s-py-2 dark:s-border-border-night dark:s-bg-muted-background-night", className) },
            React.createElement("div", null, modifiedChildren),
            isLoading && (React.createElement("div", { className: cn("s-flex s-justify-center s-py-2") },
                React.createElement(Spinner, { size: "xs" }))))));
}
const treeItemStyleClasses = {
    base: "s-group/tree s-flex s-cursor-default s-flex-row s-items-center s-gap-2 s-h-9",
    isNavigatableBase: "s-rounded-xl s-pl-1.5 s-pr-3 s-ease-out s-cursor-pointer",
    isNavigatableUnselected: cn("s-bg-sidebar-foreground/0 dark:s-bg-sidebar-foreground-night/0", "hover:s-bg-sidebar-foreground dark:hover:s-bg-sidebar-foreground-night"),
    isNavigatableSelected: cn("s-font-medium", "s-bg-sidebar-foreground dark:s-bg-sidebar-foreground-night"),
};
Tree.Item = React.forwardRef(({ label, type = "node", className = "", labelClassName = "", tailwindIconTextColor = "s-text-muted-foreground dark:s-text-muted-foreground-night", visual, checkbox, onChevronClick, collapsed, defaultCollapsed, actions, areActionsFading = true, renderTreeItems, children, isNavigatable = false, isSelected = false, onItemClick, id, }, ref) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const labelRef = React.useRef(null);
    const [collapsedState, setCollapsedState] = useState(defaultCollapsed !== null && defaultCollapsed !== void 0 ? defaultCollapsed : true);
    const isControlledCollapse = collapsed !== undefined;
    const effectiveCollapsed = isControlledCollapse
        ? collapsed
        : collapsedState;
    const effectiveOnChevronClick = isControlledCollapse
        ? onChevronClick
        : () => setCollapsedState(!collapsedState);
    const canExpand = effectiveOnChevronClick && type === "node";
    const getChildren = () => {
        if (effectiveCollapsed) {
            return [];
        }
        return typeof renderTreeItems === "function"
            ? renderTreeItems()
            : children;
    };
    const childrenToRender = getChildren();
    const checkTruncation = useCallback(() => {
        if (labelRef.current) {
            setIsTruncated(labelRef.current.scrollWidth > labelRef.current.clientWidth);
        }
    }, []);
    useEffect(() => {
        const observer = new ResizeObserver(checkTruncation);
        if (labelRef.current) {
            observer.observe(labelRef.current);
            checkTruncation();
        }
        return () => observer.disconnect();
    }, [checkTruncation]);
    const isExpanded = childrenToRender && !effectiveCollapsed;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: ref, id: id, className: cn(treeItemStyleClasses.base, onItemClick || (checkbox === null || checkbox === void 0 ? void 0 : checkbox.onCheckedChange) || canExpand
                ? "s-cursor-pointer"
                : "", isNavigatable ? treeItemStyleClasses.isNavigatableBase : "", isNavigatable
                ? isSelected
                    ? treeItemStyleClasses.isNavigatableSelected
                    : treeItemStyleClasses.isNavigatableUnselected
                : "", isExpanded ? "is-expanded" : "is-collapsed", type, className), onClick: onItemClick
                ? (e) => {
                    if (e.target instanceof HTMLElement &&
                        (e.target.closest('[role="checkbox"]') ||
                            e.target.tagName === "BUTTON")) {
                        return;
                    }
                    e.stopPropagation();
                    onItemClick();
                }
                : (e) => {
                    var _a;
                    if (!(e.target instanceof HTMLElement)) {
                        return;
                    }
                    if (e.target.tagName === "BUTTON" ||
                        e.target.closest('[role="checkbox"]')) {
                        return;
                    }
                    e.stopPropagation();
                    if (checkbox === null || checkbox === void 0 ? void 0 : checkbox.onCheckedChange) {
                        (_a = checkbox.onCheckedChange) === null || _a === void 0 ? void 0 : _a.call(checkbox, !checkbox.checked);
                    }
                    else if (canExpand) {
                        effectiveOnChevronClick();
                    }
                } },
            type === "node" && (React.createElement(Button, { icon: isExpanded ? ChevronDown : ChevronRight, size: "xmini", variant: "ghost-secondary", disabled: !effectiveOnChevronClick, onClick: (e) => {
                    e.stopPropagation();
                    if (effectiveOnChevronClick) {
                        effectiveOnChevronClick();
                    }
                } })),
            type === "leaf" && (React.createElement("div", { className: "s-w-[24px] s-flex-shrink-0" })),
            checkbox && React.createElement(Checkbox, { ...checkbox, size: "xs" }),
            React.createElement(Icon, { visual: visual, size: "sm", className: tailwindIconTextColor }),
            isTruncated ? (React.createElement(TooltipProvider, null,
                React.createElement(TooltipRoot, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement("div", { ref: labelRef, className: cn("s-font-medium s-truncate s-text-sm s-text-primary dark:s-text-primary-night", labelClassName) }, label)),
                    React.createElement(TooltipPortal, null,
                        React.createElement(TooltipContent, { side: "top", align: "start" }, label))))) : (React.createElement("div", { ref: labelRef, className: cn("s-font-medium s-truncate s-text-sm s-text-primary dark:s-text-primary-night", labelClassName) }, label)),
            actions && (React.createElement("div", { className: cn("s-flex s-grow s-gap-2", areActionsFading &&
                    "s-transform s-opacity-0 s-duration-300 group-hover/tree:s-opacity-100") }, actions))),
        React.Children.count(childrenToRender) > 0 && (React.createElement("div", { className: "s-pl-4" }, childrenToRender))));
});
Tree.Empty = function ({ label, onItemClick }) {
    return (React.createElement("div", { className: cn("s-copy-sm s-py-1.5 s-pl-6 s-italic", "s-text-muted-foreground dark:s-text-muted-foreground-night", onItemClick ? "s-cursor-pointer" : ""), onClick: onItemClick }, label));
};
//# sourceMappingURL=Tree.js.map